import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Chrome, Sun, Moon, Shield } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '../constants/legalLinks';

function Login() {
  const navigate = useNavigate();
  const { login, googleLogin, isAuthenticated, loading: authLoading, verifyMFA } = useAuth();
  const { theme, darkMode, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA / TOTP step state
  const [mfaState, setMfaState] = useState(null);   // null | { factorId }
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);

  // Redirect to profile when authenticated (already logged in, or just finished login)
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/profile', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result?.mfaRequired) {
        // Password verified — now need TOTP. Show the verification step.
        setMfaState({ factorId: result.factorId });
        setMfaCode('');
        setMfaError('');
        return;
      }
      if (result && !result.success) {
        setError(result.error || 'Invalid email or password');
      }
      // On success, navigation happens via useEffect when isAuthenticated becomes true.
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASubmit = async (e) => {
    e.preventDefault();
    setMfaError('');
    setMfaLoading(true);
    try {
      const result = await verifyMFA(mfaState.factorId, mfaCode.trim(), rememberDevice);
      if (!result?.success) {
        setMfaError(result?.error || 'Invalid code. Please try again.');
      }
      // On success, isAuthenticated becomes true → useEffect redirects to /profile.
    } finally {
      setMfaLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await googleLogin();
    } catch (err) {
      setError('Google login failed');
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.bg, color: theme.text, display: 'flex', flexDirection: 'column' }}>
      <nav style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 24px',
        borderBottom: `1px solid ${theme.border}`,
        backgroundColor: theme.card,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src="/logo.png" alt="Market Scan" style={{ width: 128, height: 128, objectFit: 'contain', borderRadius: '12px', display: 'block', backgroundColor: 'transparent' }} />
        </div>
        <button
          type="button"
          onClick={toggleTheme}
          style={{
            padding: '8px',
            backgroundColor: theme.primaryLight,
            color: theme.primary,
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={darkMode ? 'Light mode' : 'Dark mode'}
        >
          {darkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </nav>
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '12px' : '20px',
        boxSizing: 'border-box',
        minWidth: 0,
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          padding: isMobile ? '24px 16px' : '40px',
          backgroundColor: theme.card,
          borderRadius: '16px',
          boxShadow: theme.shadow,
          border: `1px solid ${theme.border}`,
          boxSizing: 'border-box',
          minWidth: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
            <img src="/logo.png" alt="Market Scan" style={{ width: 96, height: 96, objectFit: 'contain', borderRadius: '12px', display: 'block', backgroundColor: 'transparent' }} />
          </div>

          {/* ── TOTP / 2FA verification step ─────────────────────────────── */}
          {mfaState ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px', gap: '12px' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Shield size={28} color={theme.primary} />
                </div>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: theme.text, textAlign: 'center', margin: 0 }}>Two-step verification</h1>
                <p style={{ fontSize: '14px', color: theme.textMuted, textAlign: 'center', margin: 0 }}>
                  Enter the 6-digit code from your authenticator app.
                </p>
              </div>
              {mfaError && (
                <div style={{ padding: '12px', backgroundColor: theme.errorBg, border: `1px solid ${theme.errorBorder}`, borderRadius: '8px', color: theme.errorText, fontSize: '14px', marginBottom: '20px' }}>
                  {mfaError}
                </div>
              )}
              <form onSubmit={handleMFASubmit} style={{ minWidth: 0 }}>
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      fontSize: '28px',
                      fontFamily: 'monospace',
                      letterSpacing: '0.3em',
                      textAlign: 'center',
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: '8px',
                      outline: 'none',
                      backgroundColor: theme.inputBg,
                      color: theme.text,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                {/* Remember this device checkbox */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: theme.primary, cursor: 'pointer', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '13px', color: theme.textMuted }}>
                    Remember this device for 30 days
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={mfaCode.length !== 6 || mfaLoading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: (mfaCode.length !== 6 || mfaLoading) ? theme.primaryMuted : theme.primary,
                    color: theme.primaryContrast,
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (mfaCode.length !== 6 || mfaLoading) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {mfaLoading ? 'Verifying…' : (<>Verify <ArrowRight size={16} /></>)}
                </button>
              </form>
              <p style={{ fontSize: '13px', color: theme.textMuted, textAlign: 'center', marginTop: '20px' }}>
                <button
                  onClick={() => { setMfaState(null); setMfaCode(''); setMfaError(''); }}
                  style={{ background: 'none', border: 'none', color: theme.primary, fontSize: '13px', cursor: 'pointer', padding: 0 }}
                >
                  ← Back to login
                </button>
              </p>
            </>
          ) : (
          /* ── Standard email/password login ──────────────────────────── */
          <>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: theme.text, textAlign: 'center', margin: '0 0 8px 0' }}>Welcome back</h1>
            <p style={{ fontSize: '14px', color: theme.textMuted, textAlign: 'center', margin: '0 0 4px 0' }}>Sign in to continue your market research</p>
            <p style={{ fontSize: '12px', color: theme.textMuted, textAlign: 'center', margin: '0 0 32px 0', opacity: 0.9 }}>Sessions are per device—sign in on this device to access your account.</p>
            {error && (
              <div style={{ padding: '12px', backgroundColor: theme.errorBg, border: `1px solid ${theme.errorBorder}`, borderRadius: '8px', color: theme.errorText, fontSize: '14px', marginBottom: '20px' }}>
                {error}
              </div>
            )}
            <button
              onClick={handleGoogleLogin}
              style={{
                width: '100%',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                backgroundColor: theme.inputBg,
                border: `1px solid ${theme.inputBorder}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: theme.text,
                cursor: 'pointer',
                marginBottom: '20px',
              }}
            >
              <Chrome size={20} />
              Continue with Google
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: theme.border }} />
              <span style={{ fontSize: '14px', color: theme.textMuted }}>or</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: theme.border }} />
            </div>
            <form onSubmit={handleSubmit} style={{ minWidth: 0, overflow: 'hidden' }}>
              <div style={{ marginBottom: '16px', minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: theme.text, marginBottom: '6px' }}>Email</label>
                <div style={{ position: 'relative', minWidth: 0 }}>
                  <Mail size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '10px 12px 10px 40px',
                      fontSize: '14px',
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: '8px',
                      outline: 'none',
                      backgroundColor: theme.inputBg,
                      color: theme.text,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '24px', minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: theme.text, marginBottom: '6px' }}>Password</label>
                <div style={{ position: 'relative', minWidth: 0 }}>
                  <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted }} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '10px 12px 10px 40px',
                      fontSize: '14px',
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: '8px',
                      outline: 'none',
                      backgroundColor: theme.inputBg,
                      color: theme.text,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: loading ? theme.primaryMuted : theme.primary,
                  color: theme.primaryContrast,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {loading ? 'Signing in...' : (<>Sign In <ArrowRight size={16} /></>)}
              </button>
            </form>
            <p style={{ fontSize: '14px', color: theme.textMuted, textAlign: 'center', margin: '24px 0 0 0' }}>
              Don't have an account?{' '}
              <Link to="/signup" style={{ color: theme.primary, textDecoration: 'none', fontWeight: '600' }}>Sign up</Link>
            </p>
            <p style={{ fontSize: '12px', color: theme.textMuted, textAlign: 'center', marginTop: '24px' }}>
              <Link to="/" style={{ color: theme.textMuted, textDecoration: 'none' }}>← Back to home</Link>
            </p>
            <p style={{ fontSize: '11px', color: theme.textMuted, textAlign: 'center', marginTop: '16px' }}>
              By logging in you agree to our{' '}
              <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>Terms of Service</a>
              {' '}and{' '}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>Privacy Policy</a>.
            </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
